import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import UploadZone from "@/components/UploadZone";
import { Loader2, Gift, HelpCircle, PlayCircle } from "lucide-react";
import { savePhoto } from "@/services/photoStorage";
import OptionPicker from "./OptionPicker";
import { useIsMobile } from "@/hooks/use-mobile";
import ExampleCarousel from "@/components/ExampleCarousel";
import GuidedUpload from "./GuidedUpload";

interface UploadFormProps {
  brand: { id: string; name: string };
  orderId: string;
  onSubmitSuccess: () => void;
}

const UploadForm = ({ brand, orderId, onSubmitSuccess }: UploadFormProps) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [option, setOption] = useState<string | null>(null);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [showExamples, setShowExamples] = useState(false);
  const [showGuidedUpload, setShowGuidedUpload] = useState(false);

  const handleMediaCaptured = (file: File) => {
    setMediaFile(file);
    setOption("upload");
  };

  const handleMediaProcessed = () => {
    setMediaFile(null);
  };

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);

    // Create a preview URL for the selected file
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    console.log("File selected:", file.name);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!brand) {
      toast({
        title: "Error",
        description: "Invalid upload link. Please check the URL and try again.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedFile || !previewUrl) {
      toast({
        title: "Error",
        description: "Please upload a photo first",
        variant: "destructive",
      });
      return;
    }

    if (!name.trim() || !email.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const newPhoto = {
        id: `photo-${Date.now()}`,
        imageUrl: previewUrl,
        customerName: name,
        customerEmail: email,
        productName: "Product",
        submissionDate: new Date().toISOString(),
        status: "pending" as const,
        brandId: brand.id,
      };

      console.log("Starting photo upload...");
      await savePhoto(newPhoto, selectedFile);
      console.log("Photo upload completed");

      setIsLoading(false);
      onSubmitSuccess();

      toast({
        title: "Success",
        description: "Your photo has been uploaded successfully!",
      });
    } catch (error) {
      console.error("Error uploading photo:", error);
      setIsLoading(false);

      toast({
        title: "Upload Failed",
        description: "There was an error uploading your photo. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (showGuidedUpload) {
    return (
      <GuidedUpload
        onClose={() => setShowGuidedUpload(false)}
        onSubmitSuccess={onSubmitSuccess}
        brand={brand}
      />
    );
  }

  return (
    <div>
      <div className="text-center mb-2">
        <img
          // src={"https://res.cloudinary.com/dxn6haxtz/image/upload/LEGENDS_LOGO_BLACK_bj4gus.png"}
          // src={"https://res.cloudinary.com/dxn6haxtz/image/upload/v1743207389/bzyt2realt6k4e0d3q1s.png"}
          src={"https://res.cloudinary.com/dxn6haxtz/image/upload/v1743212179/CleanShot_2025-03-28_at_19.36.04_exh8h1.png"}
          alt={brand?.name || ""}
          className="max-w-[300px] h-auto mx-auto mb-2"
        />
        <h2 className="text-2xl font-bold mb-2">Get $25 store credit when you send us content.</h2>
        <p className="text-muted-foreground mb-4">
          Simply take a photo or video of your new product you received and send to us.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <Button
            variant="outline"
            onClick={() => setShowExamples(true)}
            className="w-full sm:w-auto"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Quick Examples
          </Button>
          <Button
            onClick={() => setShowGuidedUpload(true)}
            className="w-full sm:w-auto bg-primary"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            Step-by-Step Guide
          </Button>
        </div>
      </div>

      <ExampleCarousel
        isOpen={showExamples}
        onClose={() => setShowExamples(false)}
        onStartUpload={() => {
          setShowExamples(false);
          setOption("upload");
        }}
      />

      {(option || !isMobile) ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          <UploadZone
            onFileSelected={handleFileSelected}
            mediaFile={mediaFile}
            onMediaProcessed={handleMediaProcessed}
          />

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {orderId && (
              <div className="text-sm text-muted-foreground">
                Order ID: <span className="font-mono">{orderId}</span>
              </div>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading || !selectedFile}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>Submit Photo</>
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground px-6">
            By uploading, you agree to our Terms of Service and grant us permission to use your photo in our marketing
            materials.
          </p>
        </form>
      ) : (
        <OptionPicker setOption={setOption} onFileSelected={handleFileSelected} onMediaCaptured={handleMediaCaptured} />
      )}
    </div>
  );
};

export default UploadForm;
